import React, { useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { recupereBDC } from '../Services';
import BonDeCommande from '../models/BonDeCommande';
import { usePersistentState } from '../hooks/usePersistentState';

interface ListeBDCProps {
  setBonDeCommandeSelectionne: (bonDeCommande: BonDeCommande) => void;
  refreshKey: number;
}

const ListeBDC: React.FC<ListeBDCProps> = ({
  setBonDeCommandeSelectionne,
  refreshKey
}) => {
  /* ---------------- données et états ---------------- */
  const [bonsDeCommande, setBonsDeCommande] = React.useState<BonDeCommande[]>([]);

  // États persistants
  const [selectedId, setSelectedId] = usePersistentState<number | null>(
    'bdc_selectedId',
    null
  );
  const [factureFilter, setFactureFilter] = usePersistentState<'Tous' | 'Oui' | 'Non'>(
    'bdc_factureFilter',
    'Tous'
  );
  const [demandeIdFilter, setDemandeIdFilter] = usePersistentState<string>(
    'bdc_demandeIdFilter',
    ''
  );
  const [etatFilter, setEtatFilter] = usePersistentState<string>(
    'bdc_etatFilter',
    'Tous'
  );

  /* ---------------- récupération ---------------- */
  const fetchBDC = async () => {
    try {
      const bdc = await recupereBDC();
      setBonsDeCommande(bdc);
    } catch (error) {
      console.error('Erreur lors de la récupération des bons de commande:', error);
    }
  };

  useEffect(() => {
    fetchBDC();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]); // on relance uniquement si la prop change

  /* ---------------- sélection d'une ligne ---------------- */
  const handleRowClick = (id: number) => {
    setSelectedId(id);
    const bonDeCommande = bonsDeCommande.find((bdc) => bdc.id === id);
    if (bonDeCommande) setBonDeCommandeSelectionne(bonDeCommande);
  };

  /* ---------------- filtres enchaînés ---------------- */
  const filteredByFacture = bonsDeCommande.filter((bdc) => {
    if (factureFilter === 'Tous') return true;
    return factureFilter === 'Oui'
      ? bdc.envoie_facture === 'oui'
      : bdc.envoie_facture !== 'oui';
  });

  const filteredByDemandeId = filteredByFacture.filter((bdc) =>
    demandeIdFilter ? bdc.demande_ids?.includes(demandeIdFilter) : true
  );

  const filteredByEtat = filteredByDemandeId.filter((bdc) => {
    if (etatFilter === 'Tous') return true;
    return bdc.etat === etatFilter;
  });

  // Suppression d’éventuels doublons
  const uniqueBonsDeCommande = filteredByEtat.filter(
    (bdc, idx, self) => idx === self.findIndex((x) => x.id === bdc.id)
  );

  // États distincts disponibles pour le menu
  const uniqueEtats = Array.from(
    new Set(bonsDeCommande.map((bdc) => bdc.etat).filter(Boolean))
  );

  /* ---------------- rendu ---------------- */
  return (
    <div style={{ padding: 20 }}>
      {/* Filtre “Envoi facture” */}
      <FormControl variant="outlined" style={{ marginRight: 20, width: 150 }}>
        <InputLabel id="facture-filter-label">Filtrer par Facture</InputLabel>
        <Select
          labelId="facture-filter-label"
          id="facture-filter"
          value={factureFilter}
          onChange={(e) => setFactureFilter(e.target.value as 'Tous' | 'Oui' | 'Non')}
          label="Filtrer par Facture"
        >
          <MenuItem value="Tous">Tous</MenuItem>
          <MenuItem value="Oui">Oui</MenuItem>
          <MenuItem value="Non">Non</MenuItem>
        </Select>
      </FormControl>

      {/* Filtre “État” */}
      <FormControl variant="outlined" style={{ marginRight: 20, width: 150 }}>
        <InputLabel id="etat-filter-label">Filtrer par État</InputLabel>
        <Select
          labelId="etat-filter-label"
          id="etat-filter"
          value={etatFilter}
          onChange={(e) => setEtatFilter(e.target.value)}
          label="Filtrer par État"
        >
          <MenuItem value="Tous">Tous</MenuItem>
          {uniqueEtats.map((etat) => (
            <MenuItem key={etat} value={etat}>
              {etat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Recherche “demande_id” */}
      <TextField
        variant="outlined"
        label="Recherche Demande ID"
        value={demandeIdFilter}
        onChange={(e) => setDemandeIdFilter(e.target.value)}
        style={{ width: 200 }}
      />

      {/* Tableau BDC */}
      <TableContainer
        component={Paper}
        style={{ marginTop: 20, maxHeight: 500 }}
      >
        <Table stickyHeader aria-label="table des BDC">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>État</TableCell>
              <TableCell>Date paiement</TableCell>
              <TableCell>Envoi Facture</TableCell>
              <TableCell>Num Facture</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uniqueBonsDeCommande.map((bdc) => (
              <TableRow
                key={bdc.id}
                hover
                onClick={() => handleRowClick(bdc.id)}
                style={{
                  backgroundColor: selectedId === bdc.id ? '#ADD8E6' : 'inherit'
                }}
              >
                <TableCell>{bdc.id}</TableCell>
                <TableCell>{bdc.total}</TableCell>
                <TableCell>{bdc.etat}</TableCell>
                <TableCell>
                  {bdc.date_paiement
                    ? new Date(bdc.date_paiement).toLocaleDateString('fr-FR')
                    : ''}
                </TableCell>
                <TableCell>
                  {bdc.envoie_facture === 'oui' && bdc.urlfacture ? (
                    <a href={bdc.urlfacture} target="_blank" rel="noopener noreferrer">
                      Oui
                    </a>
                  ) : (
                    bdc.envoie_facture
                  )}
                </TableCell>
                <TableCell>{bdc.numfacture}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ListeBDC;
